export const FIRST_PAGE_ONLY_ATTRIBUTE = 'data-show-on-first-page'
export const LAST_PAGE_ONLY_ATTRIBUTE = 'data-show-on-last-page'
export const PRESERVE_HIDDEN_SPACE_ATTRIBUTE =
  'data-preserve-space-on-hidden'

const PAGE_RULE_BLOCK_SELECTOR = 'p'

const getReadableBlockText = (node) => {
  const text = `${node?.textContent || ''}`.replace(/\s+/g, ' ').trim()

  return text.replace(/\.{4,}.*$/, '').trim().slice(0, 100)
}

const getBlockLabel = (node, index, nextNode) => {
  const readableText = getReadableBlockText(node)
  const nextReadableText = getReadableBlockText(nextNode)

  if (readableText) return readableText
  if (nextReadableText) return `บรรทัดก่อน ${nextReadableText}`

  return `บรรทัดว่างลำดับที่ ${index + 1}`
}

const getBlockVisibleOn = (node) => {
  if (node.hasAttribute(FIRST_PAGE_ONLY_ATTRIBUTE)) return 'first'
  if (node.hasAttribute(LAST_PAGE_ONLY_ATTRIBUTE)) return 'last'

  return 'all'
}

const getPageRuleNodes = (root) =>
  Array.from(root?.querySelectorAll?.(PAGE_RULE_BLOCK_SELECTOR) || [])

export const getTextEditorPageRuleBlocks = (root) => {
  const nodes = getPageRuleNodes(root)

  return nodes.map((node, index) => ({
    index,
    label: getBlockLabel(node, index, nodes[index + 1]),
    visibleOn: getBlockVisibleOn(node),
    collapseWhenHidden: !node.hasAttribute(PRESERVE_HIDDEN_SPACE_ATTRIBUTE),
  }))
}

const clearPageRuleAttributes = (node) => {
  node.removeAttribute(FIRST_PAGE_ONLY_ATTRIBUTE)
  node.removeAttribute(LAST_PAGE_ONLY_ATTRIBUTE)
  node.removeAttribute(PRESERVE_HIDDEN_SPACE_ATTRIBUTE)
  node.classList.remove('report-first-page-only')
  node.classList.remove('report-last-page-only')
}

export const applyTextEditorPageRule = (
  root,
  {
    startIndex = 0,
    endIndex = startIndex,
    visibleOn = 'all',
    collapseWhenHidden = true,
  } = {},
) => {
  const nodes = getPageRuleNodes(root)
  if (!nodes.length) return 0

  const firstIndex = Math.max(
    0,
    Math.min(parseInt(startIndex, 10) || 0, parseInt(endIndex, 10) || 0),
  )
  const lastIndex = Math.min(
    nodes.length - 1,
    Math.max(parseInt(startIndex, 10) || 0, parseInt(endIndex, 10) || 0),
  )

  nodes.slice(firstIndex, lastIndex + 1).forEach((node) => {
    clearPageRuleAttributes(node)

    if (visibleOn === 'first') {
      node.setAttribute(FIRST_PAGE_ONLY_ATTRIBUTE, 'true')
      node.classList.add('report-first-page-only')
    } else if (visibleOn === 'last') {
      node.setAttribute(LAST_PAGE_ONLY_ATTRIBUTE, 'true')
      node.classList.add('report-last-page-only')
    }

    if (visibleOn !== 'all' && !collapseWhenHidden) {
      node.setAttribute(PRESERVE_HIDDEN_SPACE_ATTRIBUTE, 'true')
    }
  })

  return lastIndex - firstIndex + 1
}

export const clearTextEditorPageRules = (root) => {
  const nodes = getPageRuleNodes(root)
  nodes.forEach(clearPageRuleAttributes)

  return nodes.length
}

export const getTextEditorPageRuleSummary = (blocks = []) =>
  blocks
    .filter((block) => block.visibleOn !== 'all')
    .reduce((groups, block) => {
      const previous = groups[groups.length - 1]
      const canAppend =
        previous &&
        previous.endIndex + 1 === block.index &&
        previous.visibleOn === block.visibleOn &&
        previous.collapseWhenHidden === block.collapseWhenHidden

      if (canAppend) {
        previous.endIndex = block.index
        previous.endLabel = block.label
        previous.count += 1
        return groups
      }

      groups.push({
        startIndex: block.index,
        endIndex: block.index,
        startLabel: block.label,
        endLabel: block.label,
        visibleOn: block.visibleOn,
        collapseWhenHidden: block.collapseWhenHidden,
        count: 1,
      })

      return groups
    }, [])
